import _ from 'lodash';

/**
 * Locks or releases page scrolling behind modals and drawers. Adds or removes the overflow-hidden class on the first app element according to the flag.
 *
 * @param isPrevent - True to block scrolling, false to restore it.
 * @returns Nothing.
 */
export const preventAppScroll = (isPrevent: any) => {
  const appElements = document.getElementsByClassName('app');
  if (appElements?.length) {
    if (isPrevent) {
      appElements[0].classList.add('overflow-hidden');
    } else {
      appElements[0].classList.remove('overflow-hidden');
    }
  }
};

/**
 * Adds a drop shadow to the page's top container while a tab body is scrolled. Reads scrollTop from the scroll event target and toggles the --has-box-shadow class on the first top-container element.
 *
 * @param e - Scroll event carrying the scrolled element as target.
 * @returns Nothing.
 */
export const handleScrollTab = (e: any) => {
  const { scrollTop } = e.target;
  const topContainer = document.getElementsByClassName('top-container');
  if (topContainer?.length) {
    if (scrollTop === 0) {
      topContainer[0].classList.remove('--has-box-shadow');
    } else if (scrollTop !== 0) {
      topContainer[0].classList.add('--has-box-shadow');
    }
  }
};

/**
 * Walks up the DOM from a node to reach an ancestor a given number of levels higher. Steps through parentNode n times and stops early, returning the last reachable node, as soon as a level has no parent.
 *
 * @param node - Node to start climbing from.
 * @param n - Number of levels to climb.
 * @returns Ancestor reached, or the last node that still had a parent.
 */
export const getParentNode = (node: any, n = 0) => {
  let parentNode = node;
  for (let i = 0; i < n; i += 1) {
    if (parentNode?.parentNode) {
      parentNode = parentNode?.parentNode;
    } else {
      return parentNode;
    }
  }
  return parentNode;
};

/**
 * Sends the window back to the top of the page after navigation or a table change. Calls window.scrollTo with a top offset of zero.
 *
 * @returns Nothing.
 */
export const scrollTop = () => {
  window.scrollTo({ top: 0 });
};

export const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

/**
 * Reads a single query-string value off the current browser location. Parses window.location.search with URLSearchParams and returns the value for the key, or null when it is absent.
 *
 * @param key - Query-string parameter name to read.
 * @returns Parameter value, or null when the key is missing.
 */
export const getSearchParam = (key = '') => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get(key);
};

/**
 * Hides or restores the page chrome around an opened report view so the report sits on top.
 * Finds the page element by class, then toggles visibility and z-index of its top container and the
 * custom tab's ant-tabs-nav, and switches the tabs content holder's overflowY between hidden and auto.
 *
 * @param props - Options carrying pageClassName, customTabClassName, shouldHide,
 * topContainerZIndex and antTabNavZIndex.
 * @returns Nothing.
 */
export const handleHideElementsViewReport = ({
  pageClassName,
  customTabClassName,
  shouldHide = false,
  topContainerZIndex = 1,
  antTabNavZIndex = 1,
}: any) => {
  const pageElement = document.getElementsByClassName(pageClassName);
  if (pageElement.length) {
    const topContainerElement: any = _.find(pageElement[0].children, (element) =>
      _.includes(element.classList, 'top-container'),
    );
    if (topContainerElement) {
      if (shouldHide) {
        topContainerElement.style.visibility = 'hidden';
        topContainerElement.style.zIndex = 0;
      } else {
        topContainerElement.style.visibility = 'visible';
        topContainerElement.style.zIndex = topContainerZIndex;
      }
    }

    /**
     * Holds the custom tab child element found within the page element. Derived by lodash find scanning the first page element's children for one whose classList includes the custom tab class name.
     *
     * @returns The matching custom tab element or undefined.
     */
    const customTabElement = _.find(pageElement[0].children, (element) =>
      _.includes(element.classList, customTabClassName),
    );
    if (customTabElement) {
      const antTabNavElement: any = _.find(customTabElement.children, (element) =>
        _.includes(element.classList, 'ant-tabs-nav'),
      );
      if (antTabNavElement) {
        if (shouldHide) {
          antTabNavElement.style.visibility = 'hidden';
          antTabNavElement.style.zIndex = 0;
        } else {
          antTabNavElement.style.visibility = 'visible';
          antTabNavElement.style.zIndex = antTabNavZIndex;
        }
      }
    }

    const antTabsContentHolderElement: any = pageElement[0].querySelector(
      '.ant-tabs-content-holder',
    );
    if (antTabsContentHolderElement) {
      if (shouldHide) {
        antTabsContentHolderElement.style.overflowY = 'hidden';
      } else {
        antTabsContentHolderElement.style.overflowY = 'auto';
      }
    }
  }
};
